// See https://formatjs.io/docs/polyfills

import { shouldPolyfill as shouldPolyfillDisplayNames } from '@formatjs/intl-displaynames/should-polyfill'
import { shouldPolyfill as shouldPolyfillCanonical } from '@formatjs/intl-getcanonicallocales/should-polyfill'
import { shouldPolyfill as shouldPolyfillList } from '@formatjs/intl-listformat/should-polyfill'
import { shouldPolyfill as shouldPolyfillLocale } from '@formatjs/intl-locale/should-polyfill'
import { shouldPolyfill as shouldPolyfillNumber } from '@formatjs/intl-numberformat/should-polyfill'
import { shouldPolyfill as shouldPolyfillPlural } from '@formatjs/intl-pluralrules/should-polyfill'

export async function intlPolyfill(locale: string) {
  const normalizedLocale = locale.toLowerCase()
  const requests: Promise<unknown>[] = []
  /**
   * Polyfill getCanonicalLocales
   */
  if (shouldPolyfillCanonical()) {
    await import(
      /* webpackChunkName: "intl-polyfill-canonical" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      '@formatjs/intl-getcanonicallocales/polyfill'
    )
  }

  /**
   * Polyfill locale
   */
  if (shouldPolyfillLocale()) {
    await import(
      /* webpackChunkName: "intl-polyfill-locale" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      '@formatjs/intl-locale/polyfill'
    )
  }

  /**
   * Polyfill pluralization rule
   */
  const unsupportedLocalePlural = shouldPolyfillPlural(normalizedLocale)
  if (unsupportedLocalePlural) {
    await import(
      /* webpackChunkName: "intl-polyfill-force-plural" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      '@formatjs/intl-pluralrules/polyfill-force'
    )
    await import(
      /* webpackInclude: /(de|en|es|fr|ko|pt|tr|zh)(-[A-Za-z]{2})?\.js$/ */
      /* webpackChunkName: "intl-polyfill-plural" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      `@formatjs/intl-pluralrules/locale-data/${unsupportedLocalePlural}`
    )
  }

  /**
   * Polyfill list formatting
   */
  const unsupportedLocaleList = shouldPolyfillList(normalizedLocale)
  if (unsupportedLocaleList) {
    await import(
      /* webpackChunkName: "intl-polyfill-force-list" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      '@formatjs/intl-listformat/polyfill-force'
    )
    await import(
      /* webpackInclude: /(de|en|es|fr|ko|pt|tr|zh)(-[A-Za-z]{2})?\.js$/ */
      /* webpackChunkName: "intl-polyfill-list-[request]" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      `@formatjs/intl-listformat/locale-data/${unsupportedLocaleList}`
    )
  }

  /**
   * Polyfill number formatting
   */
  const unsupportedLocaleNumber = shouldPolyfillNumber(normalizedLocale)
  if (unsupportedLocaleNumber) {
    await import(
      /* webpackChunkName: "intl-polyfill-force-number" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      '@formatjs/intl-numberformat/polyfill-force'
    )
    await import(
      /* webpackInclude: /(de|en|es|fr|ko|pt|tr|zh)(-[A-Za-z]{2})?\.js$/ */
      /* webpackChunkName: "intl-polyfill-number-[request]" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      `@formatjs/intl-numberformat/locale-data/${unsupportedLocaleNumber}`
    )
  }

  /**
   * Polyfill displayNames
   */
  const unsupportedLocaleDisplayNames =
    shouldPolyfillDisplayNames(normalizedLocale)
  if (unsupportedLocaleDisplayNames) {
    await import(
      /* webpackChunkName: "intl-polyfill-force-display-names" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      '@formatjs/intl-displaynames/polyfill-force'
    )
    await import(
      /* webpackInclude: /(de|en|es|fr|ko|pt|tr|zh)(-[A-Za-z]{2})?\.js$/ */
      /* webpackChunkName: "intl-polyfill-display-names-[request]" */
      /* webpackMode: "lazy" */
      /* webpackPrefetch: false */
      /* webpackPreload: false */
      `@formatjs/intl-displaynames/locale-data/${unsupportedLocaleDisplayNames}`
    )
  }
  return requests
}
